import { pointInPolygon } from '@obraplan/core/domain/takeoff/foundation';
import {
  polygonArea,
  segmentLength,
  type Point,
  type Segment,
} from '@obraplan/core/domain/takeoff/polygon';
import {
  outlineBeamRun,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import { retanguloPorDoisCantos } from './fundacao-da-prancha';
import { traceStructure, type PlacedWall, type TracedStructure } from './plan-tracer';

/**
 * As contas do pilar e da viga com contorno na prancha, sem tela (06/10/2026).
 *
 * O pilar sem parede e o pilar e a viga em polígono guardam o contorno (`outline`); o que a
 * planta diz sobre as paredes em que eles caem continua saindo do mesmo traço de antes, para
 * que o pilar de canto e a viga deitada não mudem de comportamento. Moram à parte do desenho
 * para que o número que o painel mostra seja o que se testa sem montar a prancha.
 */

/** Quanto do polígono ainda conta como "tocando" a parede, em metros — a folga do traço. */
const FOLGA_DO_CONTORNO = 0.05;
/** O passo da amostragem ao longo da parede, em metros. */
const PASSO_DA_AMOSTRA = 0.05;

/** Os quatro cantos do retângulo que dois cantos opostos, em metros, definem. */
export function contornoDoRetangulo(a: Point, b: Point): readonly Point[] {
  return retanguloPorDoisCantos(a, b);
}

/** O retângulo envolvente do contorno: os dois cantos opostos, em metros. */
export function retanguloEnvolvente(outline: readonly Point[]): {
  readonly min: Point;
  readonly max: Point;
} {
  const xs = outline.map((ponto) => ponto.x);
  const ys = outline.map((ponto) => ponto.y);
  return {
    min: { x: Math.min(...xs), y: Math.min(...ys) },
    max: { x: Math.max(...xs), y: Math.max(...ys) },
  };
}

/** A distância do ponto ao segmento, em metros. */
function distanciaAoSegmento(ponto: Point, segmento: Segment): number {
  const dx = segmento.to.x - segmento.from.x;
  const dy = segmento.to.y - segmento.from.y;
  const quadrado = dx * dx + dy * dy;
  const t =
    quadrado === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((ponto.x - segmento.from.x) * dx + (ponto.y - segmento.from.y) * dy) / quadrado,
          ),
        );
  return Math.hypot(ponto.x - (segmento.from.x + t * dx), ponto.y - (segmento.from.y + t * dy));
}

/** O ponto está dentro do contorno, ou a uma folga da borda dele? */
function pertoDoContorno(ponto: Point, outline: readonly Point[]): boolean {
  if (pointInPolygon(ponto, outline)) return true;
  return outline.some(
    (vertice, indice) =>
      distanciaAoSegmento(ponto, { from: vertice, to: outline[(indice + 1) % outline.length]! }) <=
      FOLGA_DO_CONTORNO,
  );
}

/** A parede passa pelo contorno — por dentro dele ou encostada na borda? */
function paredeToca(parede: Segment, outline: readonly Point[]): boolean {
  const comprimento = segmentLength(parede);
  const passos = Math.max(1, Math.ceil(comprimento / PASSO_DA_AMOSTRA));
  for (let passo = 0; passo <= passos; passo += 1) {
    const t = passo / passos;
    const ponto = {
      x: parede.from.x + (parede.to.x - parede.from.x) * t,
      y: parede.from.y + (parede.to.y - parede.from.y) * t,
    };
    if (pertoDoContorno(ponto, outline)) return true;
  }
  return false;
}

/**
 * O que o contorno diz sobre a peça: a seção e as paredes em que ela cai.
 *
 * Os trechos saem do **retângulo envolvente**, pela mesma conta do traço por dois cantos, e
 * depois só ficam as paredes que o polígono de fato toca — o L de um pilar não desconta a
 * parede que passa no vazio do L, só no retângulo que o envolve.
 *
 * - **Pilar**: `sectionWidth × sectionDepth` é o retângulo envolvente, só para guardar — a
 *   conta de volume sai do contorno;
 * - **Viga**: a largura é digitada (define as pontas do contorno), e a corrida sai do
 *   perímetro (`outlineBeamRun`).
 */
export function lerContorno(
  kind: StructuralElementKind,
  outline: readonly Point[],
  larguraDaViga: number,
  paredes: readonly PlacedWall[],
): TracedStructure {
  const { min, max } = retanguloEnvolvente(outline);
  const doRetangulo = traceStructure(kind, min, max, paredes);
  const onde = new Map(paredes.map((parede) => [String(parede.wallId), parede.segment]));
  const segments = doRetangulo.segments.filter((trecho) => {
    const parede = onde.get(String(trecho.wallId));
    return parede !== undefined && paredeToca(parede, outline);
  });

  if (kind === 'PILAR') {
    return {
      sectionWidth: doRetangulo.sectionWidth,
      ...(doRetangulo.sectionDepth === undefined ? {} : { sectionDepth: doRetangulo.sectionDepth }),
      segments,
    };
  }
  return {
    sectionWidth: larguraDaViga,
    run: outlineBeamRun(outline, larguraDaViga),
    segments,
  };
}

/** A área do contorno, em m² — o rótulo e o painel dizem. */
export function areaDoContorno(outline: readonly Point[]): number {
  return polygonArea(outline);
}

/** O contorno andado `dx`, `dy` metros — o elemento arrastado. */
export function transladarContorno(
  outline: readonly Point[],
  dx: number,
  dy: number,
): readonly Point[] {
  return outline.map((ponto) => ({ x: ponto.x + dx, y: ponto.y + dy }));
}
